if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.targetButton = document.getElementById(this.dataset.targetButtonId);
        this.priceSource = document.getElementById(this.dataset.priceId);
        this.button = this.querySelector('.sticky-atc__button');
        this.priceEl = this.querySelector('.sticky-atc__price');

        if (!this.targetButton || !this.button) return;

        this.button.addEventListener('click', () => {
          if (this.targetButton.disabled) return;
          this.targetButton.click();
        });

        this.syncButtonState();
        this.buttonObserver = new MutationObserver(() => this.syncButtonState());
        this.buttonObserver.observe(this.targetButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.priceSource && this.priceEl) {
          this.syncPrice();
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        }

        // Trigger point is a fixed distance from the top of the page — how
        // tall the header (+ announcement bar) is — rather than the real
        // button's position, so the "Vis efter" / "Skjul ved" settings give
        // a single predictable line to fine-tune from, regardless of how
        // long the page is above the button on a given product.
        this.showOffset = parseInt(this.dataset.showOffset, 10) || 0;
        this.hideOffset = parseInt(this.dataset.hideOffset, 10) || 0;
        this.measureHeader();

        this.isVisible = false;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.updateVisibility();
      }

      measureHeader() {
        const header = document.querySelector('.section-header') || document.querySelector('.header');
        const rect = header ? header.getBoundingClientRect() : { bottom: 0 };
        this.headerBottom = rect.bottom + window.scrollY;
      }

      onScroll() {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.updateVisibility();
          this.scrollTicking = false;
        });
      }

      updateVisibility() {
        // Distance scrolled past the header (positive = past it).
        const distancePastHeader = window.scrollY - this.headerBottom;

        if (!this.isVisible && distancePastHeader > this.showOffset) {
          this.isVisible = true;
          this.classList.add('is-visible');
          return;
        }

        // "Skjul ved" is a gap measured back from the show point (not from
        // the header): hide once the user has scrolled back up by that many
        // pixels from where the bar appeared. A bigger value hides sooner
        // after you start scrolling up; it's always reachable (unlike
        // measuring from the header directly, which could demand scrolling
        // above the top of the page and never actually trigger).
        const hideThreshold = this.showOffset - this.hideOffset;
        if (this.isVisible && distancePastHeader < hideThreshold) {
          this.isVisible = false;
          this.classList.remove('is-visible');
        }
      }

      disconnectedCallback() {
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      syncButtonState() {
        this.button.disabled = this.targetButton.disabled;

        const targetText = this.targetButton.querySelector('span');
        const ownText = this.button.querySelector('span');
        if (targetText && ownText) ownText.textContent = targetText.textContent;

        const targetSpinner = this.targetButton.querySelector('.loading__spinner');
        const ownSpinner = this.button.querySelector('.loading__spinner');
        if (targetSpinner && ownSpinner) {
          ownSpinner.classList.toggle('hidden', targetSpinner.classList.contains('hidden'));
        }
      }

      syncPrice() {
        // Only the price the customer actually pays. Reading innerText off the
        // whole price block would also pull in its visually-hidden screen
        // reader labels ("Normalpris", "Udsalgspris", "Udsalg"), which are
        // clipped rather than display:none and so still count as rendered text.
        const sale = this.priceSource.querySelector('.price-item--sale');
        const regular = this.priceSource.querySelector('.price-item--regular');
        const current = sale?.textContent.trim() || regular?.textContent.trim();

        if (current) this.priceEl.textContent = current;
      }
    }
  );
}
